/* ==========================================================================
   Cart Drawer – Custom Elements
   Depends on: cart.js (CartAPI, updateCartCount, getSectionInnerHTML)
   ========================================================================== */

(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';

  /* ======================================================================
     <cart-drawer>
     Top-level controller for the drawer body. Handles:
       - Listening for cart:updated and opening the drawer
       - Re-rendering the drawer section after any cart change
       - Cart count updates across the page
     ====================================================================== */
  if (!customElements.get('cart-drawer')) {
    class CartDrawerElement extends HTMLElement {
      connectedCallback() {
        this.openWhenAdded =
          this.dataset.openWhenAdded === 'true';

        this._onCartUpdated = this.onCartUpdated.bind(this);
        this._onRemoveItem = this.onRemoveItem.bind(this);
        this._onChangeQty = this.onChangeQty.bind(this);

        /* Listen for add-to-cart from product-form */
        document.addEventListener('cart:updated', this._onCartUpdated);

        /* Listen for item removal / qty changes bubbling up */
        this.addEventListener('cart:remove-item', this._onRemoveItem);
        this.addEventListener('cart:change-qty', this._onChangeQty);
      }

      disconnectedCallback() {
        document.removeEventListener('cart:updated', this._onCartUpdated);
        this.removeEventListener('cart:remove-item', this._onRemoveItem);
        this.removeEventListener('cart:change-qty', this._onChangeQty);
      }

      /* --- Event handlers ------------------------------------------- */

      onCartUpdated(event) {
        var cart = event.detail?.cart;
        if (cart) {
          window.updateCartCount(cart.item_count);
          this.toggleEmpty(cart.item_count === 0);
        }

        /* Re-render section to get fresh HTML */
        this.renderSection().then(() => {
          if (this.openWhenAdded) this.openDrawer();
        });
      }

      onRemoveItem(event) {
        var line = event.detail?.line;
        if (line == null || isNaN(line)) return;
        this.updateItem(line, 0);
      }

      onChangeQty(event) {
        var line = event.detail?.line;
        var quantity = event.detail?.quantity;
        if (!line || quantity == null) return;
        this.updateItem(line, quantity);
      }

      /* --- Cart operations ------------------------------------------ */

      async updateItem(line, quantity) {
        this.classList.add('is-loading');

        try {
          var result = await CartAPI.change(
            { line: line, quantity: quantity },
            [SECTION_ID]
          );

          window.updateCartCount(result.item_count);
          this.toggleEmpty(result.item_count === 0);

          /* Update the drawer HTML from the section rendering response */
          if (result.sections && result.sections[SECTION_ID]) {
            this.updateFromSectionHTML(result.sections[SECTION_ID]);
          } else {
            await this.renderSection();
          }
        } catch (err) {
          /* Show error on the specific line item if possible */
          var itemEl = this.querySelector(
            '#CartDrawer-Item-' + line + ' [data-cart-item-error]'
          );
          if (itemEl) {
            itemEl.textContent = err.message;
          }
        } finally {
          this.classList.remove('is-loading');
        }
      }

      /* --- Section rendering --------------------------------------- */

      async renderSection() {
        try {
          var url =
            '/cart?sections=' + encodeURIComponent(SECTION_ID);
          var res = await fetch(url);
          var json = await res.json();

          if (json[SECTION_ID]) {
            this.updateFromSectionHTML(json[SECTION_ID]);
          }
        } catch (e) {
          /* silent – drawer just shows stale data */
        }
      }

      updateFromSectionHTML(html) {
        var newInner = getSectionInnerHTML(
          html,
          '[data-drawer-inner="cart"]'
        );
        var currentInner = this.querySelector(
          '[data-drawer-inner="cart"]'
        );

        if (newInner && currentInner) {
          var doc = new DOMParser().parseFromString(newInner, 'text/html');
          currentInner.replaceChildren(...doc.body.childNodes);
        }

        /* Re-check empty state from the fresh HTML */
        var itemCount = this.querySelectorAll('[data-cart-item]').length;
        this.toggleEmpty(itemCount === 0);
      }

      /* --- Drawer open / close ------------------------------------- */

      openDrawer() {
        var drawerComponent = this.closest('drawer-component');
        if (drawerComponent && drawerComponent.open) {
          drawerComponent.open();
        }
      }

      toggleEmpty(isEmpty) {
        this.classList.toggle('is-empty', isEmpty);
      }
    }

    customElements.define('cart-drawer', CartDrawerElement);
  }

  /* ======================================================================
     <cart-drawer-items>
     Handles quantity input changes inside the drawer item list.
     ====================================================================== */
  if (!customElements.get('cart-drawer-items')) {
    class CartDrawerItems extends HTMLElement {
      connectedCallback() {
        this.addEventListener('change', this.onQuantityChange.bind(this));
      }

      onQuantityChange(event) {
        var input = event.target;
        if (
          !input.matches ||
          !input.matches('.f-quantity__input')
        )
          return;

        var quantity = parseInt(input.value, 10);
        if (isNaN(quantity) || quantity < 0) return;

        var line = parseInt(input.dataset.index, 10);
        if (isNaN(line)) return;

        this.dispatchEvent(
          new CustomEvent('cart:change-qty', {
            bubbles: true,
            detail: { line: line, quantity: quantity },
          })
        );
      }
    }

    customElements.define('cart-drawer-items', CartDrawerItems);
  }

  /* ======================================================================
     <cart-recommendations-component>
     Fetches cross-sell product recommendations and injects them.
     ====================================================================== */
  if (!customElements.get('cart-recommendations-component')) {
    class CartRecommendations extends HTMLElement {
      connectedCallback() {
        this.url = this.dataset.url;
        if (this.url) this.fetchRecommendations();
      }

      async fetchRecommendations() {
        try {
          var res = await fetch(this.url);
          var html = await res.text();
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var content = doc.querySelector('.cart-drawer__cross-sell');
          if (content) {
            this.replaceChildren(...content.childNodes);
          }
        } catch (e) {
          /* silent */
        }
      }
    }

    customElements.define(
      'cart-recommendations-component',
      CartRecommendations
    );
  }

  /* ======================================================================
     Cross-sell track: drag scroll + pagination dots
     ====================================================================== */
  function initCrossSellTrack(root) {
    var track = root.querySelector('[data-cross-sell-track]');
    var dots = root.querySelectorAll('[data-cross-sell-dots] .cart-drawer__cross-sell-dot');
    if (!track) return;

    var cards = track.querySelectorAll('.cart-drawer__cross-sell-card');
    if (!cards.length) return;

    /* --- Cache card width, recalculate on resize --- */
    var cachedCardWidth = cards[0].offsetWidth;
    var resizeObserver = new ResizeObserver(function () {
      cachedCardWidth = cards[0].offsetWidth;
    });
    resizeObserver.observe(cards[0]);

    /* --- Update active dot on scroll --- */
    function updateDots() {
      if (!dots.length) return;
      var scrollLeft = track.scrollLeft;
      var idx = Math.round(scrollLeft / cachedCardWidth);
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === idx);
      });
    }

    track.addEventListener('scroll', updateDots, { passive: true });

    /* --- Click dots to navigate --- */
    dots.forEach(function (dot, i) {
      dot.style.cursor = 'pointer';
      dot.addEventListener('click', function () {
        track.scrollTo({ left: cachedCardWidth * i, behavior: 'smooth' });
      });
    });

    /* --- Mouse drag to scroll with momentum --- */
    var isDragging = false;
    var startX = 0;
    var scrollStart = 0;
    var lastX = 0;
    var velocity = 0;
    var lastTime = 0;

    function onMouseMove(e) {
      if (!isDragging) return;
      e.preventDefault();
      var now = Date.now();
      var dt = now - lastTime;
      if (dt > 0) {
        velocity = (e.pageX - lastX) / dt;
      }
      lastX = e.pageX;
      lastTime = now;
      track.scrollLeft = scrollStart - (e.pageX - startX);
    }

    function onMouseUp() {
      if (!isDragging) return;
      isDragging = false;
      track.classList.remove('is-dragging');
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);

      /* Apply momentum: snap to the card the velocity points toward */
      var cardWidth = cards[0].offsetWidth + 8; /* card + gap */
      var currentIdx = Math.round(track.scrollLeft / cardWidth);
      var targetIdx = currentIdx;

      if (Math.abs(velocity) > 0.3) {
        targetIdx = velocity < 0 ? currentIdx + 1 : currentIdx - 1;
      }
      targetIdx = Math.max(0, Math.min(cards.length - 1, targetIdx));

      track.scrollTo({ left: targetIdx * cardWidth, behavior: 'smooth' });
    }

    track.addEventListener('mousedown', function (e) {
      isDragging = true;
      startX = e.pageX;
      lastX = e.pageX;
      scrollStart = track.scrollLeft;
      velocity = 0;
      lastTime = Date.now();
      track.classList.add('is-dragging');
      e.preventDefault();
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    });
  }

  /* Selectors for containers that hold cross-sell tracks */
  var CROSS_SELL_CONTAINERS = 'cart-recommendations-component, product-recommendations';

  /* Observe new cross-sell tracks inserted via section rendering.
     Scoped to the cart drawer container instead of document.body. */
  var drawerRoot = document.querySelector('[data-section-type="cart-drawer"]');
  if (drawerRoot) {
    var observer = new MutationObserver(function () {
      drawerRoot.querySelectorAll(CROSS_SELL_CONTAINERS).forEach(function (el) {
        if (!el._trackInit && el.querySelector('[data-cross-sell-track]')) {
          el._trackInit = true;
          initCrossSellTrack(el);
        }
      });
    });
    observer.observe(drawerRoot, { childList: true, subtree: true });
  }

  /* Init any already present */
  document.querySelectorAll(CROSS_SELL_CONTAINERS).forEach(function (el) {
    if (el.querySelector('[data-cross-sell-track]')) {
      el._trackInit = true;
      initCrossSellTrack(el);
    }
  });

  /* Observe PDP product-recommendations that load content asynchronously */
  document.querySelectorAll(CROSS_SELL_CONTAINERS).forEach(function (el) {
    if (el._trackInit) return;
    if (drawerRoot && drawerRoot.contains(el)) return;
    var pdpObserver = new MutationObserver(function () {
      if (!el._trackInit && el.querySelector('[data-cross-sell-track]')) {
        el._trackInit = true;
        initCrossSellTrack(el);
      }
    });
    pdpObserver.observe(el, { childList: true, subtree: true });
  });

  /* ======================================================================
     Cross-sell quick-add buttons
     ====================================================================== */
  var crossSellRoot = document;
  crossSellRoot.addEventListener('click', function (e) {
    var btn = e.target.closest('.cart-drawer__cross-sell-add');
    if (!btn) return;

    var variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.classList.add('loading');
    btn.disabled = true;

    CartAPI.add([{ id: parseInt(variantId, 10), quantity: 1 }])
      .then(function () {
        return CartAPI.getCart();
      })
      .then(function (cart) {
        document.dispatchEvent(
          new CustomEvent('cart:updated', { detail: { cart: cart } })
        );
      })
      .catch(function () {
        /* silent */
      })
      .finally(function () {
        btn.classList.remove('loading');
        btn.disabled = false;
      });
  });
})();
